/**
 * Users API Client
 * Connects to Strapi users-permissions REST API
 */

import { getHttpClient } from '@spcc/core'
import type {
  User,
  UserStats,
  UsersFilter,
  UsersListResponse,
  PaginationMeta,
  CreateUserRequest,
  UpdateUserRequest,
  StrapiRoleType,
} from '../../domain/types'
import {
  USER_ENDPOINTS,
  type UserDto,
  type StrapiUserListResponse,
} from '../../api/api-types'
import {
  transformUserDtoToDomain,
  transformFilterToQuery,
  transformCreateUserToRequest,
  transformUpdateUserToRequest,
  calculateUserStats,
} from '../../api/transformers'

// ===========================================
// Role ID fallback constants (actual role IDs are resolved dynamically at runtime)
// ===========================================

const FALLBACK_ROLE_TYPE_TO_ID: Partial<Record<StrapiRoleType, number>> = {
  super_admin: 1,
  tenant_admin: 2,
  tenant_user: 3,
  customer: 4,
  authenticated: 5,
  public: 6,
  ecommerce: 7,
}

type RoleLookupItem = {
  id: number
  type?: string
  attributes?: {
    type?: string
  }
}

type RoleLookupResponse = {
  roles?: RoleLookupItem[]
  data?: RoleLookupItem[]
}

const roleIdCache: Partial<Record<StrapiRoleType, number>> = {}

function extractRoleItems(payload: unknown): RoleLookupItem[] {
  if (Array.isArray(payload)) {
    return payload as RoleLookupItem[]
  }

  if (!payload || typeof payload !== 'object') {
    return []
  }

  const response = payload as RoleLookupResponse
  if (Array.isArray(response.roles)) {
    return response.roles
  }
  if (Array.isArray(response.data)) {
    return response.data
  }

  return []
}

function getRoleType(item: RoleLookupItem): string | undefined {
  if (typeof item.type === 'string') {
    return item.type
  }
  if (item.attributes && typeof item.attributes.type === 'string') {
    return item.attributes.type
  }
  return undefined
}

async function resolveRoleId(httpClient: any, roleType: StrapiRoleType): Promise<number> {
  const cachedRoleId = roleIdCache[roleType]
  if (typeof cachedRoleId === 'number') {
    return cachedRoleId
  }

  try {
    const response = await httpClient.get('/api/users-permissions/roles')
    const roleItems = extractRoleItems(response.data)

    for (const item of roleItems) {
      const type = getRoleType(item)
      if (type && typeof item.id === 'number') {
        roleIdCache[type as StrapiRoleType] = item.id
      }
    }

    const resolvedRoleId = roleIdCache[roleType]
    if (typeof resolvedRoleId === 'number') {
      return resolvedRoleId
    }
  } catch {
    // Fallback below for environments where role lookup endpoint is unavailable.
  }

  const fallbackRoleId = FALLBACK_ROLE_TYPE_TO_ID[roleType]
  if (typeof fallbackRoleId === 'number') {
    return fallbackRoleId
  }

  throw new Error(`Role type "${roleType}" is not available`)
}

// ===========================================
// API Client
// ===========================================

export const usersApi = {
  /**
   * Get list of users with optional filtering and pagination
   * Uses the custom /api/admin/users endpoint which supports proper pagination
   */
  async getUsers(filter?: UsersFilter): Promise<UsersListResponse> {
    const httpClient = getHttpClient()
    const defaultFilter: UsersFilter = {
      page: 1,
      pageSize: 10,
      ...filter,
    }

    // Build query params for admin endpoint
    const params: Record<string, unknown> = {
      page: defaultFilter.page,
      pageSize: defaultFilter.pageSize,
      sort: 'createdAt:desc',
    }

    // Add filters
    if (defaultFilter.status && defaultFilter.status !== 'all') {
      params.status = defaultFilter.status
    }
    if (defaultFilter.role && defaultFilter.role !== 'all') {
      params.role = defaultFilter.role
    }
    if (defaultFilter.tenantId && defaultFilter.tenantId !== 'all') {
      params.tenantId = defaultFilter.tenantId
    }
    if (defaultFilter.search) {
      params.search = defaultFilter.search
    }
    if (defaultFilter.userType && defaultFilter.userType !== 'all') {
      params.isSystem = defaultFilter.userType === 'system' ? 'true' : 'false'
    }

    const response = await httpClient.get<StrapiUserListResponse>(
      USER_ENDPOINTS.LIST,
      { params }
    )

    // response.data is the Strapi response body: { data: [...], meta: { pagination: {...}, stats: {...} } }
    const responseBody = response.data
    const users = (responseBody.data ?? []).map(transformUserDtoToDomain)
    const meta: PaginationMeta = responseBody.meta?.pagination ?? {
      page: defaultFilter.page ?? 1,
      pageSize: defaultFilter.pageSize ?? 10,
      pageCount: 1,
      total: users.length,
    }

    const rawStats = responseBody.meta?.stats
    const globalStats: import('../../domain/types').UserStats | undefined = rawStats
      ? {
          total: rawStats.total,
          active: rawStats.active,
          inactive: rawStats.inactive,
          blocked: rawStats.blocked,
          invited: rawStats.invited,
          byRole: {},
        }
      : undefined

    return { data: users, meta, globalStats }
  },

  /**
   * Get single user by ID
   */
  async getUser(id: string): Promise<User | null> {
    const httpClient = getHttpClient()

    try {
      const response = await httpClient.get<UserDto>(
        USER_ENDPOINTS.DETAIL(id),
        { params: { 'populate[role]': true, 'populate[tenant]': true } }
      )
      return transformUserDtoToDomain(response.data)
    } catch (error) {
      // Return null if user not found
      if ((error as Error).message?.includes('Not Found')) {
        return null
      }
      throw error
    }
  },

  /**
   * Get user statistics (computed from user list)
   */
  async getUserStats(): Promise<UserStats> {
    // Fetch all users to compute stats
    const users = await usersApi.getUsers()
    return calculateUserStats(users as any)
  },

  /**
   * Create new user (invite)
   */
  async createUser(data: CreateUserRequest): Promise<User> {
    const httpClient = getHttpClient()
    const roleType = data.strapiRoleType ?? 'customer_operator'
    const roleId = await resolveRoleId(httpClient, roleType)

    const request = transformCreateUserToRequest(data, roleId)

    const response = await httpClient.post<UserDto>(
      USER_ENDPOINTS.CREATE,
      request
    )

    return transformUserDtoToDomain(response.data)
  },

  /**
   * Update user details
   */
  async updateUser(userId: string, data: UpdateUserRequest['data']): Promise<User> {
    const httpClient = getHttpClient()
    const roleId = data.strapiRoleType
      ? await resolveRoleId(httpClient, data.strapiRoleType)
      : undefined
    const normalizedFactoryId =
      data.factoryId === null ? null : data.factoryId ? Number(data.factoryId) : undefined

    const request = transformUpdateUserToRequest({
      name: data.name,
      phone: data.phone,
      userStatus: data.status,
      role: roleId,
      factory: normalizedFactoryId,
    })

    const response = await httpClient.put<UserDto>(
      USER_ENDPOINTS.UPDATE(userId),
      request
    )

    return transformUserDtoToDomain(response.data)
  },

  /**
   * Change user role
   */
  async changeUserRole(userId: string, roleType: StrapiRoleType): Promise<User> {
    const httpClient = getHttpClient()
    const roleId = await resolveRoleId(httpClient, roleType)

    const response = await httpClient.put<UserDto>(
      USER_ENDPOINTS.CHANGE_ROLE(userId),
      { role: roleId }
    )

    return transformUserDtoToDomain(response.data)
  },

  /**
   * Set user active/inactive state
   * Uses the generic update endpoint (do not use /block endpoint).
   */
  async blockUser(userId: string, blocked: boolean): Promise<User> {
    const httpClient = getHttpClient()

    const response = await httpClient.put<UserDto>(
      USER_ENDPOINTS.UPDATE(userId),
      { blocked }
    )

    return transformUserDtoToDomain(response.data)
  },

  /**
   * Unlock user account locked by too many failed login attempts.
   * Sets attemptBlocked = false on the user.
   */
  async unlockAttempts(userId: string): Promise<User> {
    const httpClient = getHttpClient()

    const response = await httpClient.put<UserDto>(
      USER_ENDPOINTS.UNLOCK_ATTEMPTS(userId)
    )

    return transformUserDtoToDomain(response.data)
  },

  /**
   * Trigger password reset email
   */
  async resetPassword(userId: string): Promise<void> {
    const httpClient = getHttpClient()

    await httpClient.post(
      USER_ENDPOINTS.RESET_PASSWORD(userId),
      {}
    )
  },

  /**
   * Delete user
   */
  async deleteUser(userId: string): Promise<void> {
    const httpClient = getHttpClient()

    await httpClient.delete(USER_ENDPOINTS.DELETE(userId))
  },
}
